import React, { useState, useEffect, useRef } from 'react';

interface AnimatedProgressBarProps {
  progress: number;
  className?: string;
  barClassName?: string;
  showPercentage?: boolean;
  duration?: number;
}

const AnimatedProgressBar: React.FC<AnimatedProgressBarProps> = ({
  progress,
  className = '',
  barClassName = '',
  showPercentage = true,
  duration = 2000
}) => {
  const [animatedProgress, setAnimatedProgress] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const barRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isVisible) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    const currentBarRef = barRef.current;
    if (currentBarRef) {
      observer.observe(currentBarRef);
    }

    return () => {
      if (currentBarRef) {
        observer.unobserve(currentBarRef);
      }
    };
  }, [isVisible]);

  useEffect(() => {
    if (!isVisible) return;

    let startTime: number;
    let animationFrame: number;

    const animate = (currentTime: number) => {
      if (!startTime) startTime = currentTime;
      const elapsed = currentTime - startTime;
      const progressRatio = Math.min(elapsed / duration, 1);
      
      // Easing function for smooth animation
      const easeOutCubic = 1 - Math.pow(1 - progressRatio, 3);
      const currentProgress = Math.floor(easeOutCubic * progress);
      
      setAnimatedProgress(currentProgress);

      if (progressRatio < 1) {
        animationFrame = requestAnimationFrame(animate);
      }
    };

    animationFrame = requestAnimationFrame(animate);

    return () => {
      if (animationFrame) {
        cancelAnimationFrame(animationFrame);
      }
    };
  }, [isVisible, progress, duration]);

  return (
    <div ref={barRef} className={`w-full ${className}`}>
      <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden relative">
        <div
          className={`h-full bg-gradient-to-r from-primary-500 to-secondary-500 rounded-full transition-all duration-300 ease-out relative overflow-hidden ${barClassName}`}
          style={{ width: `${animatedProgress}%` }}
        >
          {/* Efeito de brilho animado */}
          <div 
            className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-30 animate-pulse"
            style={{
              animation: 'shimmer 2s infinite',
              transform: 'translateX(-100%)',
              animationDelay: '0.5s'
            }}
          />
          {/* Efeito de pulso no final da barra */}
          <div 
            className="absolute top-0 right-0 w-2 h-full bg-white opacity-50 rounded-full animate-pulse"
            style={{
              animation: 'pulse 1.5s infinite',
              animationDelay: '0.3s'
            }}
          />
        </div>
      </div>
      {showPercentage && (
        <div className="flex justify-between items-center mt-2">
          <span className="text-sm text-gray-600 animate-fade-in">Progresso</span>
          <span 
            className="text-sm font-semibold text-primary-600 animate-count-up"
            style={{
              animation: isVisible ? 'countUp 0.8s ease-out' : 'none'
            }}
          >
            {animatedProgress}%
          </span>
        </div>
      )}
      
      {/* CSS para animações personalizadas */}
      <style>{`
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        
        @keyframes pulse {
          0%, 100% { opacity: 0.5; transform: scale(1); }
          50% { opacity: 0.8; transform: scale(1.1); }
        }
        
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        
        @keyframes count-up {
          from { opacity: 0; transform: scale(0.8); }
          to { opacity: 1; transform: scale(1); }
        }
        
        .animate-fade-in {
          animation: fade-in 0.6s ease-out;
        }
        
        .animate-count-up {
          animation: count-up 0.8s ease-out;
        }
      `}</style>
    </div>
  );
};

export default AnimatedProgressBar;
